/**
 * Tasks for one record. Same shape as NotesPanel: writes post to the host
 * route's action with an `intent`, and the loader revalidation refreshes it.
 *
 * `completedAt` never appears in a form — the database derives it from status,
 * so the UI only ever sends a status.
 */

import { useFetcher } from 'react-router-dom'
import type { SubjectRef, Task, TaskStatus } from '@erp/contracts'
import { Badge, Button, EmptyState, Field, Select, TextInput } from '../ui'
import { useOwnerOptions } from '../status-context'
import { formatDate } from '../format'

const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
}

const STATUS_COLOR: Record<TaskStatus, string> = {
  backlog: 'gray',
  todo: 'blue',
  in_progress: 'orange',
  done: 'green',
  cancelled: 'red',
}

const PRIORITY_COLOR: Record<string, string> = { low: 'gray', medium: 'blue', high: 'red' }

export function TasksPanel({ tasks, subject }: { tasks: readonly Task[]; subject: SubjectRef }) {
  const fetcher = useFetcher()
  const assignees = useOwnerOptions(null)
  const submitting = fetcher.state !== 'idle'

  const live = tasks.filter((task) => task.deletedAt === null)
  const open = live.filter((task) => task.status !== 'done' && task.status !== 'cancelled')
  const closed = live.filter((task) => task.status === 'done' || task.status === 'cancelled')

  return (
    <div className="ax-panel">
      <fetcher.Form method="post" className="ax-task-form">
        <input type="hidden" name="intent" value="task.create" />
        <input type="hidden" name="subjectType" value={subject.type} />
        <input type="hidden" name="subjectId" value={subject.id} />
        <Field label="New task">
          <TextInput name="title" placeholder="What needs doing?" required />
        </Field>
        <Field label="Due">
          <TextInput name="dueDate" type="datetime-local" />
        </Field>
        <Field label="Priority">
          <Select
            name="priority"
            value="medium"
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ]}
          />
        </Field>
        {/* Without this every task is created unassigned, which leaves the
            cross-entity "my open tasks" view permanently empty. */}
        <Field label="Assign to">
          <Select name="assignedTo" value="" options={assignees} />
        </Field>
        <Button variant="solid" type="submit" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add task'}
        </Button>
      </fetcher.Form>

      {live.length === 0 ? <EmptyState title="No tasks yet" /> : null}

      {open.length > 0 ? <TaskList tasks={open} /> : null}
      {closed.length > 0 ? (
        <details className="ax-task-closed">
          <summary>{closed.length} closed</summary>
          <TaskList tasks={closed} />
        </details>
      ) : null}
    </div>
  )
}

function TaskList({ tasks }: { tasks: readonly Task[] }) {
  const fetcher = useFetcher()
  const assignees = useOwnerOptions(null)

  return (
    <ul className="ax-task-list">
      {tasks.map((task) => {
        // Reflect a submitted status immediately; fall back to the server value.
        const pending = fetcher.formData
        const isThisTask = pending?.get('taskId') === task.id
        const status =
          isThisTask && typeof pending?.get('status') === 'string'
            ? (pending.get('status') as TaskStatus)
            : task.status

        return (
          <li key={task.id} className="ax-task">
            <Badge color={STATUS_COLOR[status]}>{STATUS_LABEL[status]}</Badge>
            <span className="ax-task-title">{task.title}</span>
            <Badge color={PRIORITY_COLOR[task.priority] ?? 'gray'}>{task.priority}</Badge>
            {task.dueDate ? (
              <time className="ax-task-due" dateTime={task.dueDate}>
                {formatDate(task.dueDate)}
              </time>
            ) : null}
            <Select
              name={`status-${task.id}`}
              value={status}
              options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(next) =>
                fetcher.submit(
                  { intent: 'task.status', taskId: task.id, status: next },
                  { method: 'post' },
                )
              }
            />
            <Select
              name={`assignee-${task.id}`}
              value={task.assignedTo ?? ''}
              options={assignees}
              onChange={(next) =>
                fetcher.submit(
                  { intent: 'task.assign', taskId: task.id, assignedTo: next },
                  { method: 'post' },
                )
              }
            />
          </li>
        )
      })}
    </ul>
  )
}

